import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlay, faFile } from '@fortawesome/free-solid-svg-icons';
import type { IVtsExhibitorMediaFile } from '../types';
import { formatFileSize } from '../formatBytes';
import styles from '../MediaPanel.module.scss?module';

interface MediaRowProps {
  file: IVtsExhibitorMediaFile;
  onDownload: (file: IVtsExhibitorMediaFile) => void;
}

/** One media file: preview or type icon, name, type and size, and a download link. */
export default function MediaRow({ file, onDownload }: MediaRowProps) {
  const isImage = file.mime_type_prefix === 'image';
  const isVideo = file.mime_type_prefix === 'video';

  return (
    <li className={styles.fileRow}>
      <span className={styles.fileVisual} aria-hidden="true">
        {isImage ? (
          <img src={file.link} alt="" loading="lazy" />
        ) : (
          <FontAwesomeIcon icon={isVideo ? faCirclePlay : faFile} />
        )}
      </span>
      <span className={styles.fileText}>
        <span className={styles.fileName} title={file.name}>
          {file.name}
        </span>
        <span className={styles.fileMeta}>
          {fileTypeLabel(file)} · {formatFileSize(file.size)}
        </span>
      </span>
      <a
        className={styles.secondaryButton}
        href={file.link}
        target="_blank"
        rel="noopener"
        download
        onClick={() => onDownload(file)}
      >
        Download<span className={styles.visuallyHidden}> {file.name}</span>
      </a>
    </li>
  );
}

/** Short type from the file extension (WEBP, PDF, MP4), else from the MIME type prefix. */
function fileTypeLabel(file: IVtsExhibitorMediaFile): string {
  const match = /\.([a-z0-9]{1,5})$/i.exec(file.name.trim());
  if (match) {
    return match[1].toUpperCase();
  }
  const prefix = file.mime_type_prefix.trim();

  return prefix === ''
    ? 'File'
    : prefix.charAt(0).toUpperCase() + prefix.slice(1);
}
